import httpClient from '../utils/httpClient';

const API_URL = '/payroll';

// ==================== EMPLOYEE PAYROLL CONFIG ====================

/**
 * Crear configuración de liquidación para un empleado
 * @param {Object} data - Configuración del empleado
 * @returns {Promise}
 */
export const createEmployeeConfig = async (data) => {
  const response = await httpClient.post(`${API_URL}/employee-config`, data);
  return response;
};

/**
 * Obtener configuración activa de un empleado
 * @param {number} employeeId - ID del empleado
 * @returns {Promise}
 */
export const getEmployeeConfig = async (employeeId) => {
  const response = await httpClient.get(`${API_URL}/employee-config/${employeeId}`);
  return response;
};

/**
 * Actualizar configuración de empleado
 * @param {number} configId - ID de la configuración
 * @param {Object} data - Datos a actualizar
 * @returns {Promise}
 */
export const updateEmployeeConfig = async (configId, data) => {
  const response = await httpClient.put(`${API_URL}/employee-config/${configId}`, data);
  return response;
};

/**
 * Obtener todas las configuraciones de empleados
 * @returns {Promise}
 */
export const getEmployeePayrollConfigs = async () => {
  const response = await httpClient.get(`${API_URL}/employee-configs`);
  return response;
};

/**
 * Actualizar configuración de aportes de empleado
 * @param {number} configId - ID de la configuración
 * @param {Object} data - Datos de aportes a actualizar
 * @returns {Promise}
 */
export const updateEmployeePayrollConfig = async (configId, data) => {
  const response = await httpClient.put(`${API_URL}/employee-config/${configId}`, data);
  return response;
};

/**
 * Obtener adelantos pendientes de un empleado
 * @param {number} employeeId - ID del empleado
 * @returns {Promise}
 */
export const getPendingAdvances = async (employeeId) => {
  const response = await httpClient.get(`${API_URL}/pending-advances/${employeeId}`);
  return response;
};

// ==================== PAYROLL TYPES ====================

/**
 * Obtener todos los tipos de liquidación
 * @returns {Promise}
 */
export const getAllPayrollTypes = async () => {
  const response = await httpClient.get(`${API_URL}/types`);
  return response;
};

/**
 * Crear nuevo tipo de liquidación
 * @param {Object} data - Datos del tipo
 * @returns {Promise}
 */
export const createPayrollType = async (data) => {
  const response = await httpClient.post(`${API_URL}/types`, data);
  return response;
};

// ==================== PAYROLL CONCEPTS ====================

/**
 * Obtener todos los conceptos de liquidación
 * @param {string} type - Tipo de concepto (opcional): REMUNERATION, DEDUCTION, BONUS, OTHER
 * @returns {Promise}
 */
export const getAllConcepts = async (type = null) => {
  const url = type ? `${API_URL}/concepts?type=${type}` : `${API_URL}/concepts`;
  const response = await httpClient.get(url);
  return response;
};

/**
 * Crear nuevo concepto de liquidación
 * @param {Object} data - Datos del concepto
 * @returns {Promise}
 */
export const createConcept = async (data) => {
  const response = await httpClient.post(`${API_URL}/concepts`, data);
  return response;
};

// ==================== ADVANCED PAYROLL CALCULATION ====================

/**
 * Vista previa de liquidación (sin guardar)
 * @param {Object} data - Datos para el cálculo
 * @returns {Promise}
 */
export const previewPayroll = async (data) => {
  const response = await httpClient.post(`${API_URL}/calculate-advanced/preview`, data);
  return response;
};

/**
 * Calcular liquidación avanzada con todos los detalles
 * @param {Object} data - Datos para el cálculo
 * @param {boolean} overwrite - Si debe sobreescribir liquidación existente
 * @returns {Promise}
 */
export const advancedCalculatePayroll = async (data, overwrite = false) => {
  const url = overwrite 
    ? `${API_URL}/calculate-advanced?overwrite=true` 
    : `${API_URL}/calculate-advanced`;
  const response = await httpClient.post(url, data);
  return response;
};

// ==================== VACATION MANAGEMENT ====================

/**
 * Crear o actualizar vacaciones de un empleado
 * @param {Object} data - Datos de vacaciones
 * @returns {Promise}
 */
export const createOrUpdateVacation = async (data) => {
  const response = await httpClient.post(`${API_URL}/vacations`, data);
  return response;
};

/**
 * Obtener vacaciones de un empleado
 * @param {number} employeeId - ID del empleado
 * @param {number} year - Año (opcional)
 * @returns {Promise}
 */
export const getEmployeeVacations = async (employeeId, year = null) => {
  const url = year 
    ? `${API_URL}/vacations/${employeeId}?year=${year}` 
    : `${API_URL}/vacations/${employeeId}`;
  const response = await httpClient.get(url);
  return response;
};

// ==================== GLOBAL PAYROLL RATES ====================

/**
 * Obtener configuración global de aportes
 * @returns {Promise}
 */
export const getGlobalPayrollRates = async () => {
  const response = await httpClient.get(`${API_URL}/global-rates`);
  return response;
};

/**
 * Actualizar configuración global de aportes
 * @param {Object} data - Datos de las tasas globales
 * @returns {Promise}
 */
export const updateGlobalPayrollRates = async (data) => {
  const response = await httpClient.put(`${API_URL}/global-rates`, data);
  return response;
};
